// Organizer Results Status (card 96222320; layout approved by Devin
// 2026-10-04, mockup v2). The tabulator's board for the event day: what is
// ready to publish, what is still being scored (which judge, which cards),
// what has gone out. Data: GET /feis/result-status/:id (status computed on
// the server), refreshed every minute. Publishing emails the families, so it
// is a button with a confirm, never a switch.
// The admin app carries a copy (back link and import paths differ): keep
// the two in step.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HeaderOrg from '../../../Headers/HeaderOrg';
import Loader from '../../../../Components/Loader/Loader';
import { serviceConsumer } from '../../../../network/ServiceConsumer';
import { getPrintHandler } from '../../../../helper/printHandlers';
import { formatDate } from '../../../../helper/formatDate';
import { useMaterialSymbols } from '../../../../Teacher/Oireachtas/components';
import { useCan } from '../../../../device/DeviceContext';
import { PIN_MS, afterAction, canDo, compLabel, livePins, nextExpiry, sectionsFor, statusOf, whenLabel } from './resultStatusModel';
import '../../../../Teacher/Oireachtas/oireachtas.css';
import '../../../Oireachtas/organizerOireachtas.css';
import './resultStatus.css';

const BACK = (id) => `/organizer-manage-feis?id=${id}&Manage=feis`;
const REFRESH_MS = 60 * 1000;

const api = () => process.env.REACT_APP_BASE_URL;
const messageOf = (err) => err?.messages || err?.message || 'Something went wrong. Please try again.';
const timeLabel = (d) => (d ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');

// Material Symbols are ligature text: hidden from screen readers
const Msr = ({ children }) => (
  <span className="msr" aria-hidden="true">
    {children}
  </span>
);

const ResultStatus = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const { search } = useLocation();
  const eventId = new URLSearchParams(search).get('id');

  const [data, setData] = useState(null); // { event, competitions, hasSchedule }
  const [loadedAt, setLoadedAt] = useState(null);
  const [firstError, setFirstError] = useState('');
  const [refreshError, setRefreshError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showPublished, setShowPublished] = useState(false);
  const [query, setQuery] = useState('');
  const [confirm, setConfirm] = useState(null); // { kind: publish|unpublish|publishAll, ids }
  // on an event-day device (card 540f21a4) each action follows the job:
  // entering scores, viewing complete results, publishing, unpublishing
  const can = useCan();
  const canScores = can('feis.scores.enter');
  const canView = can('feis.results.status', 'feis.results.view', 'feis.roundmedals', 'feis.results.publish', 'feis.results.unpublish');
  const canPublish = can('feis.results.publish');
  const canUnpublish = can('feis.results.unpublish');
  // an open confirmation closes when its function is unticked
  const confirmAllowed = !confirm || (confirm.kind === 'unpublish' ? canUnpublish : canPublish);
  useEffect(() => {
    if (!confirmAllowed) setConfirm(null);
  }, [confirmAllowed]);
  const [working, setWorking] = useState(null); // { kind, id, done, total } while publishing / unpublishing
  const [failure, setFailure] = useState(''); // only a refusal is reported; success shows on the row
  const [printing, setPrinting] = useState(false);
  // rows published here stay in place (to print them) for PIN_MS
  const [pinned, setPinned] = useState({});

  const pageRef = useRef(null);
  const wrapRef = useRef(null);
  const dialogRef = useRef(null);
  const searchRef = useRef(null);
  const returnFocus = useRef(null); // the button that opened the confirm
  const dataRef = useRef(null); // the latest data, for actions started from a confirm
  const requestSeq = useRef(0);
  const writing = useRef(false);
  const loadedAtRef = useRef(null);

  const load = useCallback(
    async (first = false) => {
      if (!eventId) return;
      if (!first && !loadedAtRef.current) return;
      // a publish in progress refreshes when it is done
      if (!first && writing.current) return;
      const seq = ++requestSeq.current;
      if (!first) setRefreshing(true);
      try {
        const res = await serviceConsumer('GET', `${api()}/feis/result-status/${eventId}`);
        if (seq !== requestSeq.current) return;
        setData({ event: res.event, competitions: res.competitions || [], hasSchedule: !!res.hasSchedule });
        const now = new Date();
        loadedAtRef.current = now;
        setLoadedAt(now);
        setRefreshError(false);
        setFirstError('');
      } catch (err) {
        if (seq !== requestSeq.current) return;
        if (first || !loadedAtRef.current) setFirstError(messageOf(err));
        else setRefreshError(true);
      } finally {
        if (seq === requestSeq.current) setRefreshing(false);
      }
    },
    [eventId]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  // the body takes the page's background while this page is open
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const before = document.body.style.backgroundColor;
    document.body.style.backgroundColor = getComputedStyle(page).backgroundColor;
    return () => {
      document.body.style.backgroundColor = before;
    };
  }, []);

  // refresh every minute while the page is on screen, and on coming back to it
  useEffect(() => {
    const stale = () => !loadedAtRef.current || Date.now() - loadedAtRef.current.getTime() >= REFRESH_MS - 1000;
    const tick = () => {
      if (document.visibilityState === 'visible' && stale()) load();
    };
    const timer = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  dataRef.current = data;
  const comps = data?.competitions;
  const sections = useMemo(() => sectionsFor(comps, query, pinned), [comps, query, pinned]);

  // let each pin go when its time is up (the row moves down to Published)
  useEffect(() => {
    const next = nextExpiry(pinned);
    if (next == null) return undefined;
    const timer = setTimeout(() => setPinned((p) => livePins(p, Date.now())), Math.max(0, next - Date.now()));
    return () => clearTimeout(timer);
  }, [pinned]);

  const openConfirm = (kind, list) => {
    returnFocus.current = document.activeElement;
    setConfirm({ kind, ids: list.map((c) => String(c._id)) });
  };
  // keyboard focus onto a row's last enabled button (Publish / Unpublish),
  // or the search box if that row is not shown
  const focusRow = (id) => {
    const row = id && document.querySelector(`.rs-row[data-id="${id}"]`);
    const buttons = row ? [...row.querySelectorAll('button:not([disabled])')] : [];
    (buttons[buttons.length - 1] || searchRef.current)?.focus();
  };
  const closeConfirm = () => {
    const ids = confirm?.ids || [];
    setConfirm(null);
    const back = returnFocus.current;
    returnFocus.current = null;
    // back to the button that opened it; if a refresh removed or disabled it,
    // to that competition's row as it is now
    setTimeout(() => {
      if (back && document.contains(back) && !back.disabled) back.focus();
      else focusRow(ids[0]);
    }, 0);
  };
  // what a confirm acts on now: its competitions as the latest data has them,
  // and only those still able to take the action
  const resolve = (kind, ids) => {
    const byId = new Map((dataRef.current?.competitions || []).map((c) => [String(c._id), c]));
    const now = ids.map((id) => byId.get(id)).filter(Boolean);
    return { now, eligible: now.filter((c) => canDo(kind, c)) };
  };

  // Publish / unpublish one or several, one after another; stops at the
  // first refusal and says which. Each accepted action shows on its row at
  // once; then a reload reconciles with the server. A published row stays
  // where it was (Devin: to print it); an unpublished one goes straight back
  // to the top, to Ready or In progress.
  const run = async (kind, ids) => {
    const { eligible: list } = resolve(kind, ids);
    setConfirm(null);
    // the device's job is re-read every minute: nothing it no longer has
    if (!(kind === 'unpublish' ? canUnpublish : canPublish)) return;
    returnFocus.current = null;
    setFailure('');
    if (!list.length) return;
    writing.current = true;
    requestSeq.current += 1; // a refresh already on its way is stale
    let done = 0;
    let failed = null;
    let lastId = null;
    for (const c of list) {
      const id = String(c._id);
      lastId = id;
      setWorking({ kind, id, done, total: list.length });
      try {
        await serviceConsumer('POST', `${api()}/feis/competition/scores/${kind === 'unpublish' ? 'unpublish' : 'publish'}/${c._id}`);
        done += 1;
        const at = new Date().toISOString();
        setData((d) => ({ ...d, competitions: d.competitions.map((x) => (String(x._id) === id ? afterAction(kind, x, at) : x)) }));
        if (kind !== 'unpublish') setPinned((p) => ({ ...p, [id]: { section: c.state, publishedAt: at, until: Date.now() + PIN_MS } }));
        else setPinned(({ [id]: _, ...rest }) => rest);
      } catch (err) {
        failed = { comp: c, message: messageOf(err) };
        break;
      }
    }
    if (failed) {
      const verb = kind === 'unpublish' ? 'unpublished' : 'published';
      setFailure(`${done ? `${done} of ${list.length} ${verb}. ` : ''}${failed.comp.code} was not ${verb}: ${failed.message}`);
    }
    writing.current = false;
    // the buttons stay locked until the server's own state is back (or the
    // reload fails, leaving the accepted changes shown)
    await load();
    setWorking(null);
    // keyboard users land back on the row they acted on
    setTimeout(() => focusRow(lastId), 0);
  };

  const view_ = (c) => navigate(`/complete-results/${data.event?.name}?id=${eventId}&compId=${c._id}`);
  const edit = (c) => {
    localStorage.setItem('resultStatus', 'edit');
    navigate(`/tabulate/${data.event?.name}?id=${eventId}&compId=${c._id}`);
  };
  const enter = (c) => {
    localStorage.setItem('resultStatus', 'create');
    navigate(`/tabulate/${data.event?.name}?id=${eventId}&compId=${c._id}`);
  };
  const print = (c) => getPrintHandler(`${api()}/feis/results/pdf/org/${c._id}`, setPrinting);

  const event = data?.event;
  const subtitle = event
    ? [
        event.name,
        event.feis_date &&
          formatDate(event.feis_date).toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' }),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  const busy = !!working;

  const renderRow = (c) => {
    const s = statusOf(c);
    const scored = c.scored > 0;
    const meta = [whenLabel(c), `${c.dancers} dancer${c.dancers === 1 ? '' : 's'}`].filter(Boolean).join(' · ');
    return (
      <div key={c._id} className={`rs-row is-${s.tone}`} data-testid="rs-row" data-id={String(c._id)}>
        <span className="rs-code">{c.code}</span>
        <div className="rs-what">
          <div className="rs-title">{compLabel(c)}</div>
          <div className="rs-meta">{meta}</div>
        </div>
        <div className="rs-stat">
          <span className={`rs-pill is-${s.tone}${s.warn ? ' warn' : ''}`}>
            <Msr>{s.icon}</Msr>
            {s.label}
          </span>
          {s.detail && <span className={`rs-detail${s.warn ? ' warn' : ''}`}>{s.detail}</span>}
        </div>
        <div className="rs-acts">
          {c.state === 'none' ? (
            canScores && (
              <button type="button" className="op-btn rs-wide" disabled={busy} onClick={() => enter(c)}>
                <Msr>edit_note</Msr>Enter scores
              </button>
            )
          ) : (
            <>
              {canView && (
                <button type="button" className="op-btn" disabled={!scored} onClick={() => view_(c)}>
                  <Msr>visibility</Msr>View
                </button>
              )}
              {canScores && (
                <button type="button" className="op-btn" disabled={!scored || busy} onClick={() => edit(c)}>
                  <Msr>edit</Msr>Edit
                </button>
              )}
              <button type="button" className="op-btn" disabled={!scored || printing} onClick={() => print(c)}>
                <Msr>print</Msr>Print
              </button>
              {c.state === 'published' ? (
                canUnpublish && (
                <button
                  type="button"
                  className="op-btn rs-wide"
                  disabled={busy}
                  onClick={() => openConfirm('unpublish', [c])}
                >
                  <Msr>unpublished</Msr>
                  {working?.id === String(c._id) ? 'Unpublishing…' : 'Unpublish'}
                </button>
                )
              ) : (
                canPublish && (
                <button
                  type="button"
                  className="op-btn rs-wide rs-go"
                  disabled={c.state !== 'ready' || busy}
                  title={c.state !== 'ready' ? 'Publish when every score is in' : undefined}
                  onClick={() => openConfirm('publish', [c])}
                >
                  <Msr>publish</Msr>
                  {working?.id === String(c._id) ? 'Publishing…' : 'Publish'}
                </button>
                )
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderEmpty = (icon, title, sub, action) => (
    <div className="rs-empty">
      <Msr>{icon}</Msr>
      <div className="rs-empty-t">{title}</div>
      {sub && <div className="rs-empty-s">{sub}</div>}
      {action}
    </div>
  );

  let body = null;
  if (!eventId) body = renderEmpty('error', 'No event selected', 'Open Results Status from the event’s manage page.');
  else if (!data && firstError)
    body = renderEmpty(
      'cloud_off',
      'Could not load Results Status',
      firstError,
      <button type="button" className="op-btn" onClick={() => load(true)}>
        <Msr>refresh</Msr>Try again
      </button>
    );
  else if (data && !comps.length)
    body = renderEmpty('inbox', 'No competitions with dancers yet', 'Competitions appear here once dancers are entered.');
  else if (data && !sections.length)
    body = renderEmpty('search_off', `No competition matches “${query.trim()}”`, 'Search by code, or by words in the name: reel, under 9, novice.');
  else if (data) {
    const searching = !!query.trim();
    const allPublished = !searching && sections.every((sec) => sec.state === 'published');
    body = (
      <>
        {allPublished &&
          renderEmpty('task_alt', 'Everything is published', `All ${comps.length} competitions are published.`)}
        {sections.map((sec) => {
          // a pinned row (just published / unpublished) is shown but not counted
          const inState = sec.rows.filter((c) => c.state === sec.state);
          // Published sits collapsed at the bottom; a search opens it
          const isPublished = sec.state === 'published';
          const open = !isPublished || showPublished || searching;
          return (
            <section key={sec.state} className="rs-sec" aria-label={sec.title}>
              <div className="rs-sec-h">
                <div className="rs-eyebrow" role="heading" aria-level={2}>
                  {sec.title} <span className="rs-n">{inState.length}</span>
                </div>
                {sec.state === 'ready' && inState.length > 1 && canPublish && (
                  <button
                    type="button"
                    className="op-btn rs-all"
                    disabled={busy}
                    onClick={() => openConfirm('publishAll', inState)}
                  >
                    <Msr>publish</Msr>
                    {working?.total > 1 ? `Publishing ${working.done + 1} of ${working.total}…` : `Publish all ${inState.length}`}
                  </button>
                )}
                {isPublished && !searching && (
                  <button
                    type="button"
                    className="op-btn rs-all"
                    aria-expanded={open}
                    onClick={() => setShowPublished((v) => !v)}
                  >
                    <Msr>{open ? 'expand_less' : 'expand_more'}</Msr>
                    {open ? 'Hide' : 'Show'}
                  </button>
                )}
              </div>
              {open && <div className="rs-panel">{sec.rows.map(renderRow)}</div>}
            </section>
          );
        })}
      </>
    );
  }

  // the confirm keeps the keyboard inside it, closes on Escape, and the page
  // behind it is inert while it is open
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!confirm || !wrap) return undefined;
    wrap.setAttribute('inert', '');
    wrap.setAttribute('aria-hidden', 'true');
    return () => {
      wrap.removeAttribute('inert');
      wrap.removeAttribute('aria-hidden');
    };
  }, [confirm]);
  // keep focus in the dialog, also when a refresh removes the focused button
  // (its action is no longer possible)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!confirm || !dialog || dialog.contains(document.activeElement)) return;
    dialog.querySelector('button:not([disabled])')?.focus();
  });
  const onDialogKey = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeConfirm();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = [...(dialogRef.current?.querySelectorAll('button:not([disabled])') || [])];
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const renderConfirm = () => {
    if (!confirm) return null;
    const kind = confirm.kind === 'unpublish' ? 'unpublish' : 'publish';
    // as the data is now (a refresh may have changed it since this opened)
    const { now, eligible: list } = resolve(kind, confirm.ids);
    const skipped = now.filter((c) => !canDo(kind, c));
    const one = list[0];
    let title;
    let text;
    let go = null;
    if (!list.length) {
      const codes = (now.length ? now : []).map((c) => c.code).join(', ');
      title = kind === 'unpublish' ? 'Nothing to unpublish' : 'Nothing to publish';
      text = `${codes || 'This competition'} changed since you opened this: ${kind === 'unpublish' ? 'it is not published now' : 'it is not ready to publish now'}. Nothing was sent.`;
    } else if (confirm.kind === 'unpublish') {
      title = `Unpublish ${one.code}?`;
      text = `${compLabel(one)} comes off the results page. Publishing it again emails the families again.`;
      go = 'Unpublish';
    } else if (confirm.kind === 'publish') {
      title = `Publish ${one.code} results?`;
      text = `${compLabel(one)} goes live on the results page, and the families of the ${one.dancers} dancer${one.dancers === 1 ? '' : 's'} entered get a “results published” email.`;
      go = 'Publish';
    } else {
      title = `Publish ${list.length} competition${list.length === 1 ? '' : 's'}?`;
      text = `${list.map((c) => c.code).join(', ')} go live on the results page, and families get a “results published” email for each.${skipped.length ? ` ${skipped.map((c) => c.code).join(', ')} ${skipped.length === 1 ? 'is' : 'are'} no longer ready and will be left out.` : ''}`;
      go = `Publish all ${list.length}`;
    }
    return (
      <div className="modal-scrim" onClick={closeConfirm}>
        <div
          ref={dialogRef}
          className="omodal rs-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rs-confirm-t"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={onDialogKey}
        >
          <div className="rs-modal-t" id="rs-confirm-t" role="heading" aria-level={2}>
            {title}
          </div>
          <p>{text}</p>
          <div className="modal-act">
            <button type="button" className="mbtn ghost" autoFocus onClick={closeConfirm}>
              {go ? 'Cancel' : 'Close'}
            </button>
            {go && (
              <button
                type="button"
                className={`mbtn ${confirm.kind === 'unpublish' ? 'danger' : 'rs-mgo'}`}
                onClick={() => run(kind, confirm.ids)}
              >
                {go}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page rs-page" ref={pageRef}>
        {((!data && !firstError && eventId) || printing) && <Loader />}
        <div className="wrap" ref={wrapRef}>
          <button type="button" className="op-back" onClick={() => navigate(BACK(eventId))}>
            <Msr>arrow_back</Msr>Manage event
          </button>
          <div className="rs-ph">
            <div>
              <div className="rs-h1" role="heading" aria-level={1}>
                Results Status
              </div>
              <div className="rs-sub">{subtitle || ' '}</div>
            </div>
            {loadedAt && (
              <span className={`rs-upd${refreshError ? ' rs-stale' : ''}`}>
                {refreshError
                  ? `Couldn’t refresh - showing ${timeLabel(loadedAt)}`
                  : `Updated ${timeLabel(loadedAt)} · refreshes every minute`}
              </span>
            )}
          </div>

          <div className="rs-bar">
            <label className="rs-search">
              <Msr>search</Msr>
              <input
                ref={searchRef}
                type="text"
                value={query}
                autoComplete="off"
                spellCheck={false}
                placeholder="Competition code or name"
                aria-label="Competition code or name"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setQuery('')}
              />
              {query && (
                <button type="button" className="rs-clear" title="Clear (Esc)" aria-label="Clear search" onClick={() => setQuery('')}>
                  <Msr>close</Msr>
                </button>
              )}
            </label>
            <button
              type="button"
              className="op-btn rs-refresh"
              title="Refresh"
              aria-label="Refresh"
              disabled={refreshing || !data || busy}
              onClick={() => load()}
            >
              <Msr>refresh</Msr>
              <span className="rs-lbl">Refresh</span>
            </button>
          </div>

          {failure && (
            <div className="rs-notice error" role="alert">
              <Msr>error</Msr>
              <span>{failure}</span>
              <button type="button" className="rs-clear" aria-label="Dismiss" onClick={() => setFailure('')}>
                <Msr>close</Msr>
              </button>
            </div>
          )}

          {body}
        </div>
        {renderConfirm()}
      </div>
    </>
  );
};

export default ResultStatus;
